import { Button, cn } from '@ctct/summit-core';
import { ChevronDownIcon, ChevronUpIcon } from '@ctct/summit-icons';
import { type CSSProperties, forwardRef, useRef } from 'react';

const chevronClasses = 'r:h-5 r:w-5 r:text-[theme(selectTrigger.icon.default)]';

interface ComboboxTriggerProps {
  isOpen: boolean;
  isMultiSelect?: boolean;
  disabled?: boolean;
  selected: string[];
  selectedLabels: string;
  buttonText: string;
  style?: CSSProperties & {
    '--bg'?: string;
    '--border'?: string;
    '--focus'?: string;
  };
  triggerClasses?: string;
  onSelect: (value: string) => void;
  items: { value: string; label: string }[];
}

const ComboboxTrigger = forwardRef<HTMLButtonElement, ComboboxTriggerProps>(
  (
    {
      isOpen,
      isMultiSelect,
      disabled,
      selected,
      selectedLabels,
      buttonText,
      style,
      triggerClasses,
      onSelect,
      items,
      ...props
    },
    ref
  ) => {
    const typeTimeout = useRef<NodeJS.Timeout | null>(null);
    const bufferRef = useRef('');

    const findMatch = (buffer: string) =>
      items.find((item) => item.label.toLowerCase().startsWith(buffer.toLowerCase()));

    const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      // Skip custom type to select logic for multi-select comboboxes
      if (isMultiSelect) return;

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Prevent space from triggering button click
        if (e.key === ' ') e.preventDefault();

        // If buffer is the same character and within timeout, cycle between items
        if (bufferRef.current.length === 1 && bufferRef.current === e.key) {
          const matches = items.filter((item) =>
            item.label.toLowerCase().startsWith(e.key.toLowerCase())
          );
          if (matches.length > 0) {
            const currentIndex = matches.findIndex((item) => item.value === selected[0]);
            const nextIndex = (currentIndex + 1) % matches.length;
            onSelect(matches[nextIndex].value);
          }
        } else {
          bufferRef.current += e.key;
          let match = findMatch(bufferRef.current);

          // If no match, reset buffer to just this key and try again
          if (!match) {
            bufferRef.current = e.key;
            match = findMatch(bufferRef.current);
          }
          if (match) onSelect(match.value);
        }

        if (typeTimeout.current) clearTimeout(typeTimeout.current);
        typeTimeout.current = setTimeout(() => {
          bufferRef.current = '';
        }, 500);
      }
    };

    const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

    const ButtonLabel =
      selected.length > 0 ? (
        selectedLabels
      ) : (
        <span className="r:text-(--text-placeholder)">{buttonText}</span>
      );

    return (
      <Button
        ref={ref}
        aria-label={buttonText}
        emphasis="medium"
        color="neutral"
        role="combobox"
        disabled={disabled}
        aria-expanded={isOpen}
        aria-live="polite"
        style={{
          '--sds-button-bg': style?.['--bg'],
          '--sds-button-border': style?.['--border'],
          '--sds-button-border-focus': style?.['--focus'],
        }}
        className={cn('rds-combobox-trigger', 'r:font-normal', triggerClasses)}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={0}
        data-sds-id="combobox-trigger"
        {...props}
      >
        <span className="r:truncate">{ButtonLabel}</span>
        <ChevronIcon className={cn('rds-combobox-chevron', chevronClasses)} />
      </Button>
    );
  }
);

ComboboxTrigger.displayName = 'ComboboxTrigger';

export { ComboboxTrigger };
